import React, { useState, useEffect } from 'react';
import { Sparkline, AreaChart, BarColumns, Donut, Gauge, Bullet, StatusDot } from './charts';
import { useGetMissionControl } from '../../queries/dashboard';
import {
  HiArrowUpRight,
  HiArrowDownRight,
  HiChevronDown,
  HiChevronRight,
  HiOutlineSparkles,
  HiOutlineUsers,
  HiOutlineGift,
  HiOutlineDocumentText,
  HiOutlineGlobeAsiaAustralia,
  HiOutlineServerStack,
  HiOutlineEnvelope,
  HiOutlinePresentationChartLine,
  HiOutlineClock,
} from 'react-icons/hi2';
import { baseUrl } from '../../api/baseUrl';

// All data here is REAL — sourced from /admin/dashboard/mission-control
// + live HTTP pings to the gateway and ticket-service. No mocks.

// ─── tokens ─────────────────────────────────────────────────────────
// brand color usage:
//   primary green (#34C759) → main accent
//   primaryHoverDark (#16ad3c) → editorial weight headings
//   chipBlue (#29abe2), orange (#ff8c00), chipMagenta (#c836c3),
//   chipYellow (#ffcd3f), chipRed (#cb3837), chipPurple (#8956ff) → categorical
//   secondary (#888EA8) → muted text / labels
//   border (#E0E6ED) → light rules
//   fontBlack / darkGrayBg (#111827) → primary text + hero strip
//   primaryBg (#28a74530), primaryBgLight (#28a74520) → soft surface tints

const BRAND = {
  primary: '#34C759',
  primaryDark: '#16ad3c',
  primaryBgLight: '#28a74520',
  ink: '#111827',
  secondary: '#888EA8',
  border: '#E0E6ED',
  red: '#cb3837',
  orange: '#ff8c00',
  yellow: '#ffcd3f',
  blue: '#29abe2',
  purple: '#8956ff',
  magenta: '#c836c3',
};

const fmt = n => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-IN'));
const fmtKg = n => (n === null || n === undefined ? '—' : Number(n).toFixed(1));
const ago = iso => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ${min % 60}m ago`;
  return `${Math.floor(hr / 24)}d ${hr % 24}h ago`;
};

// ─── primitives ────────────────────────────────────────────────────

// SectionShell — every Mission Control panel uses this. Now individually
// collapsible per Taniya's request. State persists per-panel via localStorage,
// keyed off the eyebrow string (which is unique per panel like "09 · User health").
export const SectionShell = ({ eyebrow, title, kicker, icon, accent = BRAND.primaryDark, action, children, defaultOpen = true }) => {
  const storageKey = `cc_mc_panel_open__${eyebrow || title}`;
  const [open, setOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored === null ? defaultOpen : stored === 'true';
    } catch (_) { return defaultOpen; }
  });
  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      try { localStorage.setItem(storageKey, String(next)); } catch (_) {}
      return next;
    });
  };
  return (
    <section className="relative w-full overflow-hidden border border-border bg-white rounded-lg">
      <header className="flex items-stretch justify-between gap-4 border-b border-border">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className="flex-1 flex items-end justify-between gap-4 px-6 pt-5 pb-4 text-left hover:bg-primaryBgLight transition-colors"
        >
          <div className="flex items-start gap-4 min-w-0">
            {icon && (
              <span
                className="flex items-center justify-center shrink-0 mt-1 rounded-md"
                style={{ width: 40, height: 40, background: BRAND.primaryBgLight, color: accent }}
              >
                <span className="text-2xl">{icon}</span>
              </span>
            )}
            <div className="min-w-0">
              <div className="font-sans uppercase text-[10px] tracking-[0.18em] text-secondary font-extrabold mb-1">{eyebrow}</div>
              <h2 className="text-2xl text-fontBlack leading-[1.1] font-extrabold tracking-tight">{title}</h2>
              {kicker && <p className="text-xs text-secondary mt-1.5 max-w-2xl font-medium">{kicker}</p>}
            </div>
          </div>
          <span className="shrink-0 flex items-center gap-2 uppercase text-[10px] tracking-[0.18em] text-secondary font-extrabold">
            {open ? 'Collapse' : 'Expand'}
            {open ? <HiChevronDown className="text-lg text-fontBlack" /> : <HiChevronRight className="text-lg text-fontBlack" />}
          </span>
        </button>
        {action && <div className="shrink-0 flex items-center pr-4">{action}</div>}
      </header>
      {open && <div className="p-6">{children}</div>}
    </section>
  );
};

export const KpiTile = ({ eyebrow, label, value, unit, sub, color = BRAND.primaryDark }) => (
  <div className="relative bg-white border border-border rounded-lg p-5 flex flex-col gap-3 min-h-[140px]">
    <div className="flex items-start justify-between">
      <div className="flex flex-col gap-1.5">
        <span className="uppercase text-[9px] tracking-[0.22em] text-secondary font-extrabold">{eyebrow}</span>
        <span className="text-sm text-fontBlack font-bold">{label}</span>
      </div>
    </div>
    <div className="flex items-end justify-between gap-3">
      <div className="text-4xl text-fontBlack leading-none font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color }}>
        {value}
        {unit && <span className="text-sm text-secondary ml-1.5 font-bold">{unit}</span>}
      </div>
    </div>
    {sub && <div className="text-xs text-secondary font-medium">{sub}</div>}
  </div>
);

// ─── 0. Mission header ────────────────────────────────────────────

export const MissionHeader = ({ environment = 'sandbox', loading = false }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const time = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const date = now.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  const env = (environment || 'sandbox').toUpperCase();
  const isProd = env === 'PRODUCTION';
  return (
    <header className="bg-darkGrayBg text-white relative overflow-hidden rounded-lg">
      <div className="relative px-6 py-7 flex flex-wrap items-end justify-between gap-6">
        <div className="flex flex-col gap-2 min-w-0">
          <div className="flex items-center gap-3 uppercase text-[10px] tracking-[0.28em] text-white/70 font-extrabold">
            <span>Master admin</span>
            <span className="opacity-50">·</span>
            <span className="flex items-center gap-1.5"><StatusDot ok={!isProd ? true : true} pulse /> {env}</span>
            <span className="opacity-50">·</span>
            <span>{loading ? 'syncing' : 'live'}</span>
          </div>
          <h1 className="text-white leading-[0.95] tracking-tight font-extrabold" style={{ fontSize: '3rem', fontWeight: 900 }}>
            Mission Control
          </h1>
          <p className="text-sm text-white/65 max-w-xl mt-1 font-medium">
            Every metric a Master Admin needs to keep the platform healthy. Data is live from the backend, refreshes every minute. The legacy snapshot is preserved below.
          </p>
        </div>
        <div className="flex flex-col items-start md:items-end gap-1">
          <div className="uppercase text-[10px] tracking-[0.22em] text-white/55 font-extrabold">Local · IST</div>
          <div className="text-white leading-none font-extrabold" style={{ fontSize: '2.4rem', fontVariantNumeric: 'tabular-nums' }}>{time}</div>
          <div className="text-xs text-white/70 font-bold">{date}</div>
        </div>
      </div>
      <div className="relative h-[2px] bg-white/15">
        <span className="absolute left-0 -top-[2px] h-[6px] w-20" style={{ background: BRAND.primary }} />
      </div>
    </header>
  );
};

// ─── 1. Pulse strip ────────────────────────────────────────────────

export const PulseStrip = ({ data }) => {
  const pulse = data?.pulse || {};
  const activeCounts = data?.activeCounts || {};
  const tiles = [
    {
      eyebrow: '01',
      label: 'Total users',
      value: fmt(pulse.totalUsers),
      sub: `${fmt(pulse.totalActiveUsers)} active`,
      color: BRAND.primaryDark,
    },
    {
      eyebrow: '02',
      label: 'Signups · last 7d',
      value: fmt(pulse.signupsThisWeek),
      sub: `${fmt(pulse.signupsToday)} today`,
      color: BRAND.blue,
    },
    {
      eyebrow: '03',
      label: 'CO₂ saved · last 7d',
      value: fmtKg(pulse.carbonThisWeekKg),
      unit: 'kg',
      sub: 'verified submissions',
      color: BRAND.orange,
    },
    {
      eyebrow: '04',
      label: 'Active challenges',
      value: fmt(activeCounts.challenges),
      sub: `${fmt(activeCounts.events)} events · ${fmt(activeCounts.campaigns)} campaigns`,
      color: BRAND.purple,
    },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
      {tiles.map((t, i) => <KpiTile key={i} {...t} />)}
    </div>
  );
};

// ─── 2. Composition (segments + activity counts) ─────────────────

export const CompositionPanel = ({ data }) => {
  const seg = data?.segments || {};
  const segments = [
    { label: 'End users', value: seg.endUsers || 0, color: BRAND.primary },
    { label: 'Green ambassadors', value: seg.ambassadors || 0, color: BRAND.orange },
    { label: 'NGO partners', value: seg.ngos || 0, color: BRAND.blue },
    { label: 'Companies', value: seg.companies || 0, color: BRAND.purple },
  ];
  const total = segments.reduce((s, x) => s + x.value, 0);
  const content = data?.contentTotals || {};
  return (
    <SectionShell
      eyebrow="01 · People & content"
      title="Who's on the platform"
      kicker="Live segment counts from User / NGO / Company collections, plus the content totals across blogs, eco-activities and tips."
      icon={<HiOutlineUsers />}
      accent={BRAND.primaryDark}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 flex flex-col items-start gap-4">
          <Donut data={segments} size={172} thickness={22} centerLabel="ACCOUNTS" centerValue={fmt(total)} />
          <ul className="w-full flex flex-col gap-2.5">
            {segments.map(s => (
              <li key={s.label} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2.5 min-w-0">
                  <span className="rounded-sm" style={{ width: 10, height: 10, background: s.color, flexShrink: 0 }} />
                  <span className="text-sm text-fontBlack truncate font-semibold">{s.label}</span>
                </span>
                <span className="text-xs text-fontBlack font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fmt(s.value)}
                  <span className="text-secondary ml-1.5 font-medium">{total ? ((s.value / total) * 100).toFixed(1) : '0'}%</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-7 grid grid-cols-2 gap-3 content-start">
          <div className="border border-border rounded-lg bg-white p-4 col-span-2">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Library — content totals</div>
            <div className="grid grid-cols-3 gap-4 mt-2">
              <div>
                <div className="text-3xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(content.blogs)}</div>
                <div className="text-xs text-secondary font-bold mt-0.5">Blogs</div>
              </div>
              <div>
                <div className="text-3xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(content.ecoActivities)}</div>
                <div className="text-xs text-secondary font-bold mt-0.5">Eco-activities</div>
              </div>
              <div>
                <div className="text-3xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(content.tips)}</div>
                <div className="text-xs text-secondary font-bold mt-0.5">Tips</div>
              </div>
            </div>
          </div>
          <div className="border border-border rounded-lg bg-white p-4">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Active challenges</div>
            <div className="text-3xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.primaryDark }}>{fmt(data?.activeCounts?.challenges)}</div>
          </div>
          <div className="border border-border rounded-lg bg-white p-4">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Active events</div>
            <div className="text-3xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.blue }}>{fmt(data?.activeCounts?.events)}</div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
};

// ─── 3. Activity (signups + submissions by day) ───────────────────

export const ActivityPanel = ({ data }) => {
  const signups = (data?.signupsByDay || []).map(d => ({
    label: new Date(d._id).toLocaleDateString('en-IN', { weekday: 'short' }),
    value: d.count,
  }));
  const submissions = (data?.submissionsByDay || []).map(d => ({
    label: new Date(d._id).toLocaleDateString('en-IN', { weekday: 'short' }),
    a: d.submitted,
    b: d.verified,
  }));
  const totalSubs = (data?.submissionsByDay || []).reduce((s, d) => s + d.submitted, 0);
  const totalVer = (data?.submissionsByDay || []).reduce((s, d) => s + d.verified, 0);
  return (
    <SectionShell
      eyebrow="02 · Activity"
      title="Signups & submissions · last 7 days"
      kicker="User signup velocity and challenge-submission throughput, IST-grouped per day from the User and UserChallenges collections."
      icon={<HiOutlineSparkles />}
      accent={BRAND.blue}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-6 min-w-0">
          <div className="flex items-baseline justify-between mb-2">
            <h4 className="text-base text-fontBlack font-extrabold">Signups per day</h4>
            <span className="uppercase text-[10px] tracking-[0.18em] text-secondary font-extrabold">
              {fmt(data?.pulse?.signupsThisWeek)} this week
            </span>
          </div>
          <AreaChart data={signups} width={640} height={170} color={BRAND.primaryDark} showAxis yLabel="USERS / DAY" emptyLabel="No signups in window" />
        </div>
        <div className="lg:col-span-6 min-w-0">
          <div className="flex items-baseline justify-between mb-2">
            <h4 className="text-base text-fontBlack font-extrabold">Submissions vs verifications</h4>
            <div className="flex items-center gap-3 uppercase text-[10px] tracking-[0.14em] text-secondary font-extrabold">
              <span className="flex items-center gap-1.5"><span className="rounded-sm" style={{ width: 10, height: 10, background: BRAND.blue }} /> Submitted</span>
              <span className="flex items-center gap-1.5"><span className="rounded-sm" style={{ width: 10, height: 10, background: BRAND.orange, opacity: 0.9 }} /> Verified</span>
            </div>
          </div>
          <BarColumns data={submissions} width={640} height={170} colorA={BRAND.blue} colorB={BRAND.orange} emptyLabel="No submissions in window" />
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="border-t border-border pt-3">
              <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Total submitted</div>
              <div className="text-2xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(totalSubs)}</div>
            </div>
            <div className="border-t border-border pt-3">
              <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Total verified</div>
              <div className="text-2xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmt(totalVer)}
                <span className="text-sm text-secondary font-bold ml-1.5">
                  {totalSubs ? `(${((totalVer / totalSubs) * 100).toFixed(0)}%)` : ''}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
};

// ─── 4. Economy (coupons) ────────────────────────────────────────

export const EconomyPanel = ({ data }) => {
  const c = data?.coupons || {};
  const total = (c.assigned || 0) + (c.redeemed || 0) + (c.unassigned || 0);
  const breakdown = [
    { label: 'Assigned', value: c.assigned || 0, color: BRAND.blue },
    { label: 'Redeemed', value: c.redeemed || 0, color: BRAND.primaryDark },
    { label: 'Unassigned', value: c.unassigned || 0, color: BRAND.secondary },
  ];
  const redemptionRate = (c.assigned + c.redeemed) ? Math.round((c.redeemed / (c.assigned + c.redeemed)) * 100) : 0;
  return (
    <SectionShell
      eyebrow="03 · Rewards economy"
      title="Coupon inventory & redemption"
      kicker="State of every coupon in the UserCoupon collection. Redemption rate = redeemed / (assigned + redeemed)."
      icon={<HiOutlineGift />}
      accent={BRAND.orange}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 flex flex-col items-start gap-4">
          <Donut data={breakdown} size={172} thickness={22} centerLabel="COUPONS" centerValue={fmt(total)} />
          <ul className="w-full flex flex-col gap-2.5">
            {breakdown.map(s => (
              <li key={s.label} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2.5 min-w-0">
                  <span className="rounded-sm" style={{ width: 10, height: 10, background: s.color, flexShrink: 0 }} />
                  <span className="text-sm text-fontBlack truncate font-semibold">{s.label}</span>
                </span>
                <span className="text-xs text-fontBlack font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fmt(s.value)}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-7 flex flex-col gap-4">
          <div className="border border-border rounded-lg bg-white p-4">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-2">Redemption rate</div>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.primaryDark }}>{redemptionRate}</span>
              <span className="text-sm text-secondary font-bold">%</span>
            </div>
            <div className="mt-3"><Bullet value={redemptionRate} max={100} color={BRAND.primary} height={6} /></div>
            <div className="text-xs text-secondary font-medium mt-2">
              {fmt(c.redeemed)} redeemed out of {fmt((c.assigned || 0) + (c.redeemed || 0))} non-stock coupons
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="border border-border rounded-lg bg-white p-4">
              <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Assigned</div>
              <div className="text-2xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.blue }}>{fmt(c.assigned)}</div>
            </div>
            <div className="border border-border rounded-lg bg-white p-4">
              <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Redeemed</div>
              <div className="text-2xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.primaryDark }}>{fmt(c.redeemed)}</div>
            </div>
            <div className="border border-border rounded-lg bg-white p-4">
              <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">In stock</div>
              <div className="text-2xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(c.unassigned)}</div>
            </div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
};

// ─── 5. Notification health ──────────────────────────────────────

export const NotificationHealthPanel = ({ data }) => {
  const n = data?.notification24h || {};
  const sent = n.sent || 0;
  const failed = n.failed || 0;
  const queued = n.queued || 0;
  const total = sent + failed + queued + (n.sending || 0) + (n.retrying || 0);
  const deliveryRate = total ? Math.round((sent / total) * 100) : 0;
  const channels = [
    { label: 'Sent', value: sent, color: BRAND.primaryDark },
    { label: 'Failed', value: failed, color: BRAND.red },
    { label: 'Queued', value: queued, color: BRAND.yellow },
  ];
  const byDay = (data?.notificationByDay || []).map(d => ({
    label: new Date(d._id).toLocaleDateString('en-IN', { weekday: 'short' }),
    a: d.sent,
    b: d.failed,
  }));
  const reminders = data?.reminderCrons || [];
  const expectedReminders = [
    'event_reminder_24hr',
    'event_reminder_1hr',
    'challenge_expiring_soon',
    'campaign_expiring_soon',
    'reward_expiring_soon',
    'sponsored_blog_expiring_soon',
  ];
  const reminderMap = Object.fromEntries(reminders.map(r => [r._id, r]));
  const reminderRows = expectedReminders.map(type => ({
    type,
    last: reminderMap[type]?.lastFiredAt,
    total: reminderMap[type]?.totalFired || 0,
  }));
  const broadcastsEnabled = data?.broadcastsEnabled;
  return (
    <SectionShell
      eyebrow="04 · Notification system"
      title="Delivery health & broadcast gate"
      kicker="ZeptoMail / FCM / in-app delivery counts from NotificationLog (last 24h). The EMAIL_BROADCASTS_ENABLED kill-switch state and the 6 reminder crons' last-fire timestamps from EmailReminderSent."
      icon={<HiOutlineEnvelope />}
      accent={BRAND.blue}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 flex flex-col items-start gap-4">
          <Donut data={channels.filter(c => c.value > 0)} size={172} thickness={22} centerLabel="LAST 24H" centerValue={fmt(total)} />
          <ul className="w-full flex flex-col gap-2.5">
            {channels.map(c => (
              <li key={c.label} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2.5 min-w-0">
                  <span className="rounded-sm" style={{ width: 10, height: 10, background: c.color, flexShrink: 0 }} />
                  <span className="text-sm text-fontBlack truncate font-semibold">{c.label}</span>
                </span>
                <span className="text-xs text-fontBlack font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(c.value)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-4 flex flex-col gap-4">
          <Gauge value={deliveryRate} max={100} size={170} thickness={14} color={deliveryRate > 80 ? BRAND.primary : deliveryRate > 50 ? BRAND.orange : BRAND.red} label="DELIVERY · 24H" unit="%" />
          <div className="border border-border rounded-lg bg-white p-3 flex items-center justify-between gap-3">
            <div className="flex flex-col gap-1 min-w-0">
              <span className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold">Bulk broadcast gate</span>
              <span className="text-sm text-fontBlack font-extrabold truncate">EMAIL_BROADCASTS_ENABLED</span>
            </div>
            <span
              className="uppercase text-[10px] tracking-[0.18em] px-2 py-1 rounded font-extrabold"
              style={{
                background: broadcastsEnabled ? BRAND.primaryBgLight : '#cb383726',
                color: broadcastsEnabled ? BRAND.primaryDark : BRAND.red,
              }}
            >{broadcastsEnabled ? 'ON' : 'OFF'}</span>
          </div>
          <div className="border border-border rounded-lg bg-white p-3">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-2">Send · last 7d</div>
            <BarColumns data={byDay} width={420} height={110} colorA={BRAND.primaryDark} colorB={BRAND.red} emptyLabel="No notifications in window" />
          </div>
        </div>
        <div className="lg:col-span-4 flex flex-col gap-1.5">
          <div className="uppercase text-[10px] tracking-[0.18em] text-secondary font-extrabold mb-1">Scheduled reminder crons</div>
          {reminderRows.map((r, i) => (
            <div key={i} className="flex items-center justify-between gap-2 border-b border-border py-2">
              <div className="flex items-center gap-2 min-w-0">
                <StatusDot ok={!!r.last} />
                <span className="text-[11px] text-fontBlack truncate font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>{r.type}</span>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] text-secondary font-bold">{r.last ? ago(r.last) : 'never fired'}</div>
                <div className="text-[10px] text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(r.total)} total</div>
              </div>
            </div>
          ))}
          {!broadcastsEnabled && (
            <p className="text-[11px] text-secondary font-medium mt-2">
              Reminders are gated by the bulk-broadcast switch above — flip it ON to start firing.
            </p>
          )}
        </div>
      </div>
    </SectionShell>
  );
};

// ─── 6. Moderation queue ─────────────────────────────────────────

export const ModerationPanel = ({ data }) => {
  const m = data?.moderation || {};
  const items = [
    { type: 'Help / support issues (open)', value: m.openHelpIssues || 0, color: BRAND.red },
    { type: 'Account deletion requests', value: m.pendingDeletions || 0, color: BRAND.blue },
    { type: 'Pending blogs', value: m.pendingBlogs || 0, color: BRAND.orange },
    { type: 'New campaign requests', value: m.pendingCampaignRequests || 0, color: BRAND.purple },
    { type: 'Campaign modification requests', value: m.pendingModifyRequests || 0, color: BRAND.magenta },
    { type: 'Proposed challenges', value: m.pendingProposedChallenges || 0, color: BRAND.yellow },
    { type: 'Partner support tickets', value: m.openSupportTickets || 0, color: BRAND.primaryDark },
  ];
  const total = items.reduce((s, i) => s + i.value, 0);
  const max = Math.max(1, ...items.map(i => i.value));
  return (
    <SectionShell
      eyebrow="05 · Moderation queue"
      title="Pending review across categories"
      kicker={`${fmt(total)} item${total === 1 ? '' : 's'} waiting on a decision across blogs / deletions / campaign requests / proposed challenges / partner tickets.`}
      icon={<HiOutlineDocumentText />}
      accent={BRAND.orange}
    >
      <div className="flex flex-col gap-3">
        {items.map(q => (
          <div key={q.type} className="grid grid-cols-12 items-center gap-3 border border-border rounded-lg bg-white px-4 py-3">
            <div className="col-span-5 flex items-center gap-3">
              <span style={{ width: 4, height: 32, background: q.color, borderRadius: 2 }} />
              <div className="text-sm text-fontBlack font-bold">{q.type}</div>
            </div>
            <div className="col-span-5"><Bullet value={q.value} max={max} color={q.color} height={6} /></div>
            <div className="col-span-2 flex items-center justify-end">
              <span className="text-2xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(q.value)}</span>
            </div>
          </div>
        ))}
      </div>
    </SectionShell>
  );
};

// ─── 7. Top contributing challenges by carbon ────────────────────

export const CarbonImpactPanel = ({ data }) => {
  const top = data?.topChallengesByCarbon || [];
  const total = data?.pulse?.totalUsers ? null : null;
  const maxKg = Math.max(1, ...top.map(t => t.totalCarbonKg || 0));
  return (
    <SectionShell
      eyebrow="06 · Carbon impact"
      title="Top contributing challenges"
      kicker="Top 5 challenges ranked by total verified carbon savings (kg CO₂e), aggregated from UserChallenges where reviewStatus = submitted."
      icon={<HiOutlineGlobeAsiaAustralia />}
      accent={BRAND.primaryDark}
    >
      {top.length === 0 ? (
        <div className="text-center text-secondary text-sm py-8 font-medium">No verified submissions yet.</div>
      ) : (
        <div className="flex flex-col gap-3">
          {top.map((t, i) => (
            <div key={t.challengeId} className="grid grid-cols-12 items-center gap-3">
              <span className="col-span-1 text-[11px] tracking-[0.18em] text-secondary font-extrabold">{String(i + 1).padStart(2, '0')}</span>
              <span className="col-span-4 text-base text-fontBlack font-extrabold truncate">{t.name || `challenge · ${t.challengeId?.slice(-6)}`}</span>
              <span className="col-span-2 text-xs text-secondary font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{t.contributors} contributor{t.contributors === 1 ? '' : 's'}</span>
              <span className="col-span-3"><Bullet value={t.totalCarbonKg} max={maxKg} color={BRAND.primary} height={6} /></span>
              <span className="col-span-2 text-right text-sm text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmtKg(t.totalCarbonKg)} <span className="text-secondary font-bold">kg</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </SectionShell>
  );
};

// ─── 8. System health (live HTTP pings) ──────────────────────────

export const SystemHealthPanel = () => {
  const endpoints = [
    { name: 'gateway', url: `${baseUrl}/health` },
    { name: 'ticket-service', url: `${baseUrl}/tickets-api/health` },
  ];
  const [status, setStatus] = useState(() => Object.fromEntries(endpoints.map(e => [e.name, { code: null, ms: null }])));
  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      const next = {};
      for (const ep of endpoints) {
        const t0 = performance.now();
        try {
          const res = await fetch(ep.url, { cache: 'no-store' });
          const ms = Math.round(performance.now() - t0);
          next[ep.name] = { code: res.status, ms };
        } catch (e) {
          next[ep.name] = { code: 0, ms: null };
        }
      }
      if (mounted) setStatus(next);
    };
    tick();
    const t = setInterval(tick, 30_000);
    return () => { mounted = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <SectionShell
      eyebrow="07 · System health"
      title="Live endpoint pings"
      kicker={`Browser-side health pings to ${baseUrl}. Refreshes every 30 seconds. Latency shown is end-to-end (including your network).`}
      icon={<HiOutlineServerStack />}
      accent={BRAND.secondary}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {endpoints.map(ep => {
          const s = status[ep.name];
          const ok = s?.code === 200;
          return (
            <div key={ep.name} className="border border-border rounded-lg bg-white p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <StatusDot ok={ok} pulse={ok} />
                <div className="min-w-0">
                  <div className="text-base text-fontBlack font-extrabold truncate">{ep.name}</div>
                  <div className="text-xs text-secondary font-bold truncate" style={{ fontVariantNumeric: 'tabular-nums' }}>{ep.url.replace(baseUrl, '')}</div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: ok ? BRAND.primaryDark : BRAND.red }}>
                  {s?.code === null ? '…' : s.code}
                </div>
                <div className="text-[10px] text-secondary font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{s?.ms !== null && s?.ms !== undefined ? `${s.ms}ms` : ''}</div>
              </div>
            </div>
          );
        })}
      </div>
    </SectionShell>
  );
};

// ─── Master Card ─────────────────────────────────────────────────

export const MasterCard = ({ children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border border-border bg-white rounded-lg">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-primaryBgLight transition-colors"
      >
        <div className="flex items-start gap-4 min-w-0">
          <span
            className="flex items-center justify-center shrink-0 mt-1 rounded-md"
            style={{ width: 40, height: 40, background: BRAND.primaryBgLight, color: BRAND.primaryDark }}
          >
            <HiOutlinePresentationChartLine className="text-2xl" />
          </span>
          <div className="min-w-0">
            <div className="uppercase text-[10px] tracking-[0.18em] text-secondary font-extrabold mb-1">00 · Legacy snapshot</div>
            <h2 className="text-2xl text-fontBlack leading-[1.1] font-extrabold tracking-tight">Original dashboard view</h2>
            <p className="text-xs text-secondary mt-1.5 max-w-2xl font-medium">
              Snapshot · Track Impact Matrix · Submission Stats · Referrals · Leaderboard · ToDo · Expiring Campaigns · Expiring Events. The full original screen, preserved.
            </p>
          </div>
        </div>
        <span className="shrink-0 flex items-center gap-3 uppercase text-[10px] tracking-[0.18em] text-secondary font-extrabold">
          {open ? 'Collapse' : 'Expand'}
          {open ? <HiChevronDown className="text-lg text-fontBlack" /> : <HiChevronRight className="text-lg text-fontBlack" />}
        </span>
      </button>
      {open && (
        <div className="border-t border-border p-6 bg-white">
          {children}
        </div>
      )}
    </section>
  );
};

// ─── EXTENDED PANELS ─────────────────────────────────────────────

// 09 · User Health Detail — verification, countries, blocked
export const UserHealthPanel = ({ data }) => {
  const h = data?.userHealth || {};
  const countries = data?.usersByCountry || [];
  const maxC = Math.max(1, ...countries.map(c => c.count || 0));
  const verifiedRate = data?.pulse?.totalUsers ? Math.round((h.verified / data.pulse.totalUsers) * 100) : 0;
  return (
    <SectionShell
      eyebrow="09 · User health"
      title="Verification, geography, banned"
      kicker="Email-verified ratio, top 5 countries by user count, and counts of deleted/blocked accounts. Country data from User.location.country."
      icon={<HiOutlineUsers />}
      accent={BRAND.blue}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 grid grid-cols-2 gap-3">
          <div className="border border-border rounded-lg bg-white p-4 col-span-2">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-2">Email verified · ratio</div>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-4xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: verifiedRate > 50 ? BRAND.primaryDark : BRAND.red }}>{verifiedRate}</span>
              <span className="text-sm text-secondary font-bold">%</span>
              <span className="text-xs text-secondary font-medium ml-2">{fmt(h.verified)} of {fmt(data?.pulse?.totalUsers)}</span>
            </div>
            <Bullet value={verifiedRate} max={100} color={verifiedRate > 50 ? BRAND.primary : BRAND.red} height={6} />
          </div>
          <div className="border border-border rounded-lg bg-white p-4">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Deleted</div>
            <div className="text-2xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(h.deleted)}</div>
          </div>
          <div className="border border-border rounded-lg bg-white p-4">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Blocked</div>
            <div className="text-2xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: h.blocked > 0 ? BRAND.red : '#111827' }}>{fmt(h.blocked)}</div>
          </div>
        </div>
        <div className="lg:col-span-7">
          <h4 className="text-base text-fontBlack font-extrabold mb-3">Top 5 countries</h4>
          {countries.length === 0 ? (
            <div className="text-secondary text-sm font-medium py-4">No country data yet.</div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {countries.map((c, i) => (
                <div key={c._id || i} className="grid grid-cols-12 items-center gap-3">
                  <span className="col-span-1 text-[11px] tracking-[0.18em] text-secondary font-extrabold">{String(i + 1).padStart(2, '0')}</span>
                  <span className="col-span-4 text-sm text-fontBlack font-bold truncate">{c._id || 'Unknown'}</span>
                  <span className="col-span-5"><Bullet value={c.count} max={maxC} color={BRAND.blue} height={6} /></span>
                  <span className="col-span-2 text-right text-sm text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(c.count)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SectionShell>
  );
};

// 10 · Engagement Detail — top challenges + events + approval rate
export const EngagementDetailPanel = ({ data }) => {
  const topCh = data?.topChallengesBySubmissions || [];
  const topEv = data?.topEventsByRegistrations || [];
  const ap = data?.submissionApproval || { submitted: 0, verified: 0 };
  const approvalRate = ap.submitted ? Math.round((ap.verified / ap.submitted) * 100) : 0;
  const maxC = Math.max(1, ...topCh.map(c => c.submissions || 0));
  const maxE = Math.max(1, ...topEv.map(e => e.registrations || 0));
  return (
    <SectionShell
      eyebrow="10 · Engagement detail"
      title="Top challenges, top events, approval rate"
      kicker="Lifetime submission counts per challenge, lifetime registration counts per event, and the overall submission approval rate from UserChallenges."
      icon={<HiOutlineSparkles />}
      accent={BRAND.orange}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4">
          <div className="border border-border rounded-lg bg-white p-5">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-2">Approval rate · lifetime</div>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-5xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: approvalRate > 50 ? BRAND.primaryDark : BRAND.red }}>{approvalRate}</span>
              <span className="text-base text-secondary font-bold">%</span>
            </div>
            <Bullet value={approvalRate} max={100} color={approvalRate > 50 ? BRAND.primary : BRAND.red} height={6} />
            <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-border">
              <div>
                <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold">Submitted</div>
                <div className="text-2xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(ap.submitted)}</div>
              </div>
              <div>
                <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold">Verified</div>
                <div className="text-2xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.primaryDark }}>{fmt(ap.verified)}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-4">
          <h4 className="text-base text-fontBlack font-extrabold mb-3">Top challenges by submissions</h4>
          {topCh.length === 0 ? (
            <div className="text-secondary text-sm font-medium">No challenge submissions yet.</div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {topCh.map((c, i) => (
                <div key={c.challengeId || i} className="grid grid-cols-12 items-center gap-2">
                  <span className="col-span-1 text-[11px] tracking-[0.18em] text-secondary font-extrabold">{String(i + 1).padStart(2, '0')}</span>
                  <span className="col-span-6 text-xs text-fontBlack font-bold truncate">{c.name || `· ${c.challengeId?.slice(-6)}`}</span>
                  <span className="col-span-3"><Bullet value={c.submissions} max={maxC} color={BRAND.orange} height={5} /></span>
                  <span className="col-span-2 text-right text-xs text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(c.submissions)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-4">
          <h4 className="text-base text-fontBlack font-extrabold mb-3">Top events by registrations</h4>
          {topEv.length === 0 ? (
            <div className="text-secondary text-sm font-medium">No event registrations yet.</div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {topEv.map((e, i) => (
                <div key={e.eventId || i} className="grid grid-cols-12 items-center gap-2">
                  <span className="col-span-1 text-[11px] tracking-[0.18em] text-secondary font-extrabold">{String(i + 1).padStart(2, '0')}</span>
                  <span className="col-span-6 text-xs text-fontBlack font-bold truncate">{e.name || `· ${e.eventId?.slice(-6)}`}</span>
                  <span className="col-span-3"><Bullet value={e.registrations} max={maxE} color={BRAND.purple} height={5} /></span>
                  <span className="col-span-2 text-right text-xs text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(e.registrations)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SectionShell>
  );
};

// 11 · Points Economy Detail — earned/redeemed + top performers
export const PointsEconomyPanel = ({ data }) => {
  const p = data?.pointsEconomy || {};
  const topEarners = data?.topEarners || [];
  const topRedeemers = data?.topRedeemers || [];
  const netFlow = (p.earnedThisWeek || 0) - (p.redeemedThisWeek || 0);
  return (
    <SectionShell
      eyebrow="11 · Points economy"
      title="Distribution vs redemption — all-time & this week"
      kicker="Points earned (positive UserPointAllocation entries) vs redeemed (sum of pointsRequired across redeemed UserCoupons). Top 5 earners and top 5 redeemers by activity."
      icon={<HiOutlineGift />}
      accent={BRAND.primaryDark}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 grid grid-cols-2 gap-3">
          <div className="border border-border rounded-lg bg-white p-4">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Earned · all-time</div>
            <div className="text-2xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.primaryDark }}>{fmt(p.earnedAllTime)}</div>
          </div>
          <div className="border border-border rounded-lg bg-white p-4">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Redeemed · all-time</div>
            <div className="text-2xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.orange }}>{fmt(p.redeemedAllTime)}</div>
          </div>
          <div className="border border-border rounded-lg bg-white p-4">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Earned · this week</div>
            <div className="text-2xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(p.earnedThisWeek)}</div>
          </div>
          <div className="border border-border rounded-lg bg-white p-4">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Redeemed · this week</div>
            <div className="text-2xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(p.redeemedThisWeek)}</div>
          </div>
          <div className="border border-border rounded-lg bg-white p-4 col-span-2">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Net flow · this week</div>
            <div className="text-3xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: netFlow >= 0 ? BRAND.primaryDark : BRAND.red }}>
              {netFlow >= 0 ? '+' : ''}{fmt(netFlow)}
            </div>
            <div className="text-xs text-secondary font-medium mt-1">{netFlow >= 0 ? 'Inflating · more earned than redeemed' : 'Deflating · more redeemed than earned'}</div>
          </div>
        </div>
        <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm text-fontBlack font-extrabold mb-2">Top earners (by balance)</h4>
            {topEarners.length === 0 ? (
              <div className="text-secondary text-xs font-medium">No data.</div>
            ) : (
              <ul className="flex flex-col">
                {topEarners.map((u, i) => (
                  <li key={u._id || i} className="grid grid-cols-12 items-center gap-2 py-2 border-b border-border">
                    <span className="col-span-1 text-[11px] tracking-[0.18em] text-secondary font-extrabold">{String(i + 1).padStart(2, '0')}</span>
                    <span className="col-span-8 text-xs text-fontBlack font-bold truncate">{u.username || u.email || '—'}</span>
                    <span className="col-span-3 text-right text-xs text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(u.points)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <h4 className="text-sm text-fontBlack font-extrabold mb-2">Top redeemers (by count)</h4>
            {topRedeemers.length === 0 ? (
              <div className="text-secondary text-xs font-medium">No data.</div>
            ) : (
              <ul className="flex flex-col">
                {topRedeemers.map((u, i) => (
                  <li key={u.userId || i} className="grid grid-cols-12 items-center gap-2 py-2 border-b border-border">
                    <span className="col-span-1 text-[11px] tracking-[0.18em] text-secondary font-extrabold">{String(i + 1).padStart(2, '0')}</span>
                    <span className="col-span-8 text-xs text-fontBlack font-bold truncate">{u.username || `user · ${(u.userId || '').toString().slice(-6)}`}</span>
                    <span className="col-span-3 text-right text-xs text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(u.redemptions)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </SectionShell>
  );
};

// 12 · Notification Detail — channel + top failing types
export const NotificationDetailPanel = ({ data }) => {
  const byCh = data?.notifByChannel || [];
  const byType = data?.notifByType || [];
  const topFail = data?.notifTopFailing || [];
  const channelColors = { email: BRAND.primaryDark, push: BRAND.blue, 'in-app': BRAND.purple, sms: BRAND.magenta, whatsapp: BRAND.primary };
  const channelData = byCh.map(c => ({ label: c._id, value: c.count, color: channelColors[c._id] || BRAND.secondary }));
  const channelTotal = channelData.reduce((s, c) => s + c.value, 0);
  return (
    <SectionShell
      eyebrow="12 · Notification detail"
      title="By channel & top failing types"
      kicker="Last 24h send counts split by channel (email / push / in-app / sms / whatsapp). Plus the top 5 notification types causing the most failures in the last 7 days — the queue you want to triage first."
      icon={<HiOutlineEnvelope />}
      accent={BRAND.purple}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 flex flex-col items-start gap-3">
          <Donut data={channelData} size={172} thickness={22} centerLabel="LAST 24H" centerValue={fmt(channelTotal)} />
          <ul className="w-full flex flex-col gap-2">
            {channelData.map(c => (
              <li key={c.label} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2.5 min-w-0">
                  <span className="rounded-sm" style={{ width: 10, height: 10, background: c.color, flexShrink: 0 }} />
                  <span className="text-sm text-fontBlack truncate font-semibold capitalize">{c.label}</span>
                </span>
                <span className="text-xs text-fontBlack font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(c.value)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-4">
          <h4 className="text-base text-fontBlack font-extrabold mb-3">Top send / fail · per type (7d)</h4>
          {byType.length === 0 ? <div className="text-secondary text-sm font-medium">No data.</div> : (
            <ul className="flex flex-col">
              {byType.map((t, i) => (
                <li key={t._id || i} className="grid grid-cols-12 items-center gap-2 py-1.5 border-b border-border">
                  <span className="col-span-7 text-[11px] text-fontBlack font-bold truncate">{t._id}</span>
                  <span className="col-span-2 text-right text-[11px] text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.primaryDark }}>{fmt(t.sent)}</span>
                  <span className="col-span-3 text-right text-[11px] text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: t.failed > 0 ? BRAND.red : BRAND.secondary }}>{fmt(t.failed)}</span>
                </li>
              ))}
              <li className="grid grid-cols-12 items-center gap-2 py-1.5 mt-1">
                <span className="col-span-7 uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold">type</span>
                <span className="col-span-2 text-right uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold">sent</span>
                <span className="col-span-3 text-right uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold">failed</span>
              </li>
            </ul>
          )}
        </div>
        <div className="lg:col-span-4">
          <h4 className="text-base text-fontBlack font-extrabold mb-3">Top failing types (7d)</h4>
          {topFail.length === 0 ? <div className="text-secondary text-sm font-medium">No failures · last 7 days.</div> : (
            <div className="flex flex-col gap-2.5">
              {topFail.map((t, i) => {
                const maxF = Math.max(1, ...topFail.map(x => x.count));
                return (
                  <div key={t._id || i} className="grid grid-cols-12 items-center gap-2">
                    <span className="col-span-1 text-[11px] tracking-[0.18em] text-secondary font-extrabold">{String(i + 1).padStart(2, '0')}</span>
                    <span className="col-span-6 text-[11px] text-fontBlack font-bold truncate">{t._id}</span>
                    <span className="col-span-3"><Bullet value={t.count} max={maxF} color={BRAND.red} height={5} /></span>
                    <span className="col-span-2 text-right text-xs text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.red }}>{fmt(t.count)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </SectionShell>
  );
};

// 13 · Content velocity + blog status
export const ContentVelocityPanel = ({ data }) => {
  const v = data?.contentVelocity || {};
  const blogsByStatus = data?.blogsByStatus || [];
  const statusColors = { approved: BRAND.primaryDark, active: BRAND.primary, pending: BRAND.yellow, rejected: BRAND.red, draft: BRAND.secondary, 'auto approved': BRAND.blue };
  return (
    <SectionShell
      eyebrow="13 · Content velocity"
      title="What's being created & published"
      kicker="Created-this-week counts per content type, plus the full status breakdown for blogs (the heaviest content moderation pipeline)."
      icon={<HiOutlineDocumentText />}
      accent={BRAND.magenta}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 grid grid-cols-3 gap-3 content-start">
          <div className="border border-border rounded-lg bg-white p-4">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Blogs · 7d</div>
            <div className="text-3xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.magenta }}>{fmt(v.blogsThisWeek)}</div>
            <div className="text-[10px] text-secondary font-bold mt-1">of {fmt(data?.contentTotals?.blogs)} total</div>
          </div>
          <div className="border border-border rounded-lg bg-white p-4">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Eco · 7d</div>
            <div className="text-3xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.primaryDark }}>{fmt(v.ecoActivitiesThisWeek)}</div>
            <div className="text-[10px] text-secondary font-bold mt-1">of {fmt(data?.contentTotals?.ecoActivities)} total</div>
          </div>
          <div className="border border-border rounded-lg bg-white p-4">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Tips · 7d</div>
            <div className="text-3xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.blue }}>{fmt(v.tipsThisWeek)}</div>
            <div className="text-[10px] text-secondary font-bold mt-1">of {fmt(data?.contentTotals?.tips)} total</div>
          </div>
        </div>
        <div className="lg:col-span-7">
          <h4 className="text-base text-fontBlack font-extrabold mb-3">Blogs by status</h4>
          {blogsByStatus.length === 0 ? (
            <div className="text-secondary text-sm font-medium">No blog data.</div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {blogsByStatus.map((s, i) => {
                const max = Math.max(1, ...blogsByStatus.map(x => x.count));
                return (
                  <div key={s._id || i} className="grid grid-cols-12 items-center gap-3">
                    <span className="col-span-3 text-sm text-fontBlack font-bold capitalize">{s._id || 'unspecified'}</span>
                    <span className="col-span-7"><Bullet value={s.count} max={max} color={statusColors[s._id] || BRAND.secondary} height={6} /></span>
                    <span className="col-span-2 text-right text-sm text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(s.count)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </SectionShell>
  );
};

// 14 · Issues / Help by category
export const IssuesPanel = ({ data }) => {
  const cats = data?.issuesByCategory || [];
  const total = data?.helpIssuesTotal || 0;
  const resolved24h = data?.issuesResolved24h || 0;
  const maxC = Math.max(1, ...cats.map(c => c.count || 0));
  return (
    <SectionShell
      eyebrow="14 · Help & support"
      title="Issue mix & resolution velocity"
      kicker="Categories of help/issue tickets raised by users, plus how many got resolved in the last 24 hours."
      icon={<HiOutlineClock />}
      accent={BRAND.red}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 grid grid-cols-2 gap-3 content-start">
          <div className="border border-border rounded-lg bg-white p-4 col-span-2">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Total issues raised</div>
            <div className="text-3xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(total)}</div>
          </div>
          <div className="border border-border rounded-lg bg-white p-4">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Open</div>
            <div className="text-2xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.red }}>{fmt(data?.moderation?.openHelpIssues)}</div>
          </div>
          <div className="border border-border rounded-lg bg-white p-4">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-1">Resolved · 24h</div>
            <div className="text-2xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.primaryDark }}>{fmt(resolved24h)}</div>
          </div>
        </div>
        <div className="lg:col-span-8">
          <h4 className="text-base text-fontBlack font-extrabold mb-3">By category</h4>
          {cats.length === 0 ? (
            <div className="text-secondary text-sm font-medium">No issue data yet.</div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {cats.map((c, i) => (
                <div key={c._id || i} className="grid grid-cols-12 items-center gap-3">
                  <span className="col-span-5 text-sm text-fontBlack font-bold truncate">{c._id || 'Uncategorized'}</span>
                  <span className="col-span-5"><Bullet value={c.count} max={maxC} color={BRAND.red} height={6} /></span>
                  <span className="col-span-2 text-right text-sm text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(c.count)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SectionShell>
  );
};

// 15 · Carbon by category + trees planted
export const CarbonByCategoryPanel = ({ data }) => {
  const byCat = data?.carbonByCategory || [];
  const totalTrees = data?.totalTreesPlanted || 0;
  const maxKg = Math.max(1, ...byCat.map(c => c.totalCarbonKg || 0));
  return (
    <SectionShell
      eyebrow="15 · Carbon by category"
      title="Where the impact is concentrated"
      kicker="Verified carbon savings (kg CO₂e) grouped by Challenge.category, plus total tree-plantation records. Top 6 categories shown."
      icon={<HiOutlineGlobeAsiaAustralia />}
      accent={BRAND.primaryDark}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4">
          <div className="border border-border rounded-lg bg-white p-5">
            <div className="uppercase text-[9px] tracking-[0.18em] text-secondary font-extrabold mb-2">Total trees planted</div>
            <div className="text-5xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.primaryDark }}>{fmt(totalTrees)}</div>
            <div className="text-xs text-secondary font-medium mt-1">From TreePlantation collection</div>
          </div>
        </div>
        <div className="lg:col-span-8">
          <h4 className="text-base text-fontBlack font-extrabold mb-3">By category</h4>
          {byCat.length === 0 ? (
            <div className="text-secondary text-sm font-medium">No carbon-by-category data yet.</div>
          ) : (
            <div className="flex flex-col gap-3">
              {byCat.map((c, i) => (
                <div key={c._id || i} className="grid grid-cols-12 items-center gap-3">
                  <span className="col-span-1 text-[11px] tracking-[0.18em] text-secondary font-extrabold">{String(i + 1).padStart(2, '0')}</span>
                  <span className="col-span-4 text-sm text-fontBlack font-bold truncate">{c._id || 'Uncategorised'}</span>
                  <span className="col-span-2 text-xs text-secondary font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{c.submissions} sub.</span>
                  <span className="col-span-3"><Bullet value={c.totalCarbonKg} max={maxKg} color={BRAND.primary} height={6} /></span>
                  <span className="col-span-2 text-right text-sm text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {fmtKg(c.totalCarbonKg)} <span className="text-secondary font-bold">kg</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SectionShell>
  );
};

// 16 · Recent activity feed
export const RecentActivityPanel = ({ data }) => {
  const ra = data?.recentActivity || {};
  const sections = [
    { label: 'Last signups', color: BRAND.blue, items: (ra.recentSignups || []).map(u => ({ at: u.createdAt, line: u.username || u.email })) },
    { label: 'Last submissions', color: BRAND.orange, items: (ra.recentSubmissions || []).map(s => ({ at: s.updatedAt, line: `· ${s.reviewStatus || 'pending'} · challenge ${(s.challengeId || '').toString().slice(-6)}` })) },
    { label: 'Last deletions', color: BRAND.red, items: (ra.recentDeletions || []).map(d => ({ at: d.createdAt, line: d.fullName || d.deletionReason || `user ${(d.userId || '').toString().slice(-6)}` })) },
    { label: 'Last help issues', color: BRAND.magenta, items: (ra.recentIssues || []).map(i => ({ at: i.createdAt, line: `${i.status === 'resolved' ? '✓ ' : ''}${i.title || 'no title'}` })) },
    { label: 'Last blogs', color: BRAND.primaryDark, items: (ra.recentBlogs || []).map(b => ({ at: b.createdAt, line: `${b.status ? `[${b.status}] ` : ''}${b.title || 'untitled'}` })) },
  ];
  return (
    <SectionShell
      eyebrow="16 · Activity feed"
      title="What just happened on the platform"
      kicker="Last 5 events per collection: signups, submissions, account deletions, help issues, blogs. Useful as a heartbeat — quiet feed = quiet platform."
      icon={<HiOutlineClock />}
      accent={BRAND.secondary}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        {sections.map(sec => (
          <div key={sec.label}>
            <div className="flex items-center gap-2 mb-2.5 pb-2 border-b border-border">
              <span className="rounded-sm" style={{ width: 8, height: 8, background: sec.color }} />
              <span className="uppercase text-[10px] tracking-[0.16em] text-fontBlack font-extrabold">{sec.label}</span>
            </div>
            {sec.items.length === 0 ? (
              <div className="text-secondary text-xs font-medium py-2">No recent.</div>
            ) : (
              <ul className="flex flex-col gap-2">
                {sec.items.map((it, i) => (
                  <li key={i} className="flex flex-col gap-0.5 py-1.5 border-b border-border">
                    <span className="text-xs text-fontBlack font-bold truncate">{it.line || '—'}</span>
                    <span className="text-[10px] text-secondary font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{ago(it.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </SectionShell>
  );
};

// 17 · Referrals — total invited users, this-week velocity, top referrers, by-day trend
export const ReferralsPanel = ({ data }) => {
  const r = data?.referrals || {};
  const totalReferred = r.totalReferredUsers || 0;
  const thisWeek = r.referredThisWeek || 0;
  const top = r.topReferrers || [];
  const byDay = r.referralsByDay || [];

  const totalUsers = data?.pulse?.totalUsers || 0;
  const referralRate = totalUsers > 0 ? Math.round((totalReferred / totalUsers) * 100) : 0;
  const maxByDay = Math.max(1, ...byDay.map((d) => d.count || 0));
  const maxReferralCount = Math.max(1, ...top.map((t) => t.referralCount || 0));

  return (
    <SectionShell
      eyebrow="17 · Referrals"
      title="Word-of-mouth growth"
      kicker="How many users signed up because someone shared their referral code. Counts and last-7-day trend from User.referredBy.userId."
      icon={<HiOutlineSparkles />}
      accent={BRAND.magenta}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Headline KPIs */}
        <div className="lg:col-span-4 flex flex-col gap-3">
          <div className="border border-border rounded-lg bg-white p-4">
            <div className="uppercase text-[9px] tracking-[0.22em] text-secondary font-extrabold mb-1">Total referred</div>
            <div className="text-4xl text-fontBlack font-extrabold leading-none" style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.magenta }}>
              {fmt(totalReferred)}
            </div>
            <div className="text-xs text-secondary mt-2 font-medium">
              {referralRate}% of all {fmt(totalUsers)} users came via referral
            </div>
          </div>
          <div className="border border-border rounded-lg bg-white p-4">
            <div className="uppercase text-[9px] tracking-[0.22em] text-secondary font-extrabold mb-1">This week</div>
            <div className="text-3xl text-fontBlack font-extrabold leading-none" style={{ fontVariantNumeric: 'tabular-nums', color: thisWeek > 0 ? BRAND.primaryDark : BRAND.secondary }}>
              {fmt(thisWeek)}
            </div>
            <div className="text-xs text-secondary mt-2 font-medium">New users via referral · last 7 days</div>
          </div>
        </div>

        {/* Top referrers */}
        <div className="lg:col-span-5">
          <h4 className="text-base text-fontBlack font-extrabold mb-3">Top referrers</h4>
          {top.length === 0 ? (
            <div className="text-secondary text-sm font-medium py-4">No referrals yet.</div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {top.map((t, i) => {
                const name = [t.firstName, t.lastName].filter(Boolean).join(' ') || t.username || t.email || '(unknown user)';
                return (
                  <div key={(t._id || i).toString()} className="grid grid-cols-12 items-center gap-3">
                    <span className="col-span-1 text-[11px] tracking-[0.18em] text-secondary font-extrabold">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="col-span-5 min-w-0">
                      <div className="text-sm text-fontBlack font-bold truncate">{name}</div>
                      {t.referralCode && (
                        <div className="text-[10px] text-secondary font-bold uppercase tracking-wider" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {t.referralCode}
                        </div>
                      )}
                    </span>
                    <span className="col-span-4">
                      <Bullet value={t.referralCount} max={maxReferralCount} color={BRAND.magenta} height={6} />
                    </span>
                    <span className="col-span-2 text-right text-sm text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {fmt(t.referralCount)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* By-day trend */}
        <div className="lg:col-span-3">
          <h4 className="text-base text-fontBlack font-extrabold mb-3">Last 7 days</h4>
          {byDay.length === 0 ? (
            <div className="text-secondary text-sm font-medium py-4">No referrals in the last week.</div>
          ) : (
            <div className="flex flex-col gap-2">
              {byDay.map((d) => (
                <div key={d._id} className="grid grid-cols-12 items-center gap-2">
                  <span className="col-span-4 text-[10px] tracking-[0.12em] text-secondary font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {d._id?.slice(5) || '—'}
                  </span>
                  <span className="col-span-6">
                    <Bullet value={d.count} max={maxByDay} color={BRAND.magenta} height={4} />
                  </span>
                  <span className="col-span-2 text-right text-xs text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {fmt(d.count)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SectionShell>
  );
};

// 18 · Platform breakdown — Apple vs Android (derived from AdImpression.deviceType)
export const PlatformPanel = ({ data }) => {
  const p = data?.platform || {};
  const [window, setWindow] = useState('last30Days');
  const breakdown = p[window] || { android: 0, ios: 0, web: 0, unknown: 0, total: 0 };

  const total = breakdown.total || 0;
  const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);
  const androidPct = pct(breakdown.android);
  const iosPct = pct(breakdown.ios);
  const webPct = pct(breakdown.web);
  const unknownPct = pct(breakdown.unknown);

  const tiles = [
    { key: 'android', label: 'Android', count: breakdown.android, pct: androidPct, color: BRAND.primary },
    { key: 'ios', label: 'iOS / Apple', count: breakdown.ios, pct: iosPct, color: BRAND.blue },
    { key: 'web', label: 'Web', count: breakdown.web, pct: webPct, color: BRAND.purple },
    { key: 'unknown', label: 'Unknown', count: breakdown.unknown, pct: unknownPct, color: BRAND.secondary },
  ];

  const windowLabel = { last7Days: 'last 7 days', last30Days: 'last 30 days', allTime: 'all time' }[window];

  return (
    <SectionShell
      eyebrow="18 · Platform mix"
      title="Apple vs Android · who's using what"
      kicker="Unique users seen per device type (android / iOS / web) from AdImpression — every ad view tags the platform. Switch the window to see weekly vs monthly vs all-time."
      icon={<HiOutlineGlobeAsiaAustralia />}
      accent={BRAND.blue}
      action={
        <div className="flex items-center gap-1 border border-border rounded-md p-0.5 bg-white">
          {['last7Days', 'last30Days', 'allTime'].map((w) => (
            <button
              key={w}
              onClick={() => setWindow(w)}
              className={`px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.12em] rounded transition-colors ${
                window === w
                  ? 'bg-primaryBgLight text-primary'
                  : 'text-secondary hover:text-fontBlack'
              }`}
            >
              {w === 'last7Days' ? '7d' : w === 'last30Days' ? '30d' : 'all'}
            </button>
          ))}
        </div>
      }
    >
      {total === 0 ? (
        <div className="text-secondary text-sm font-medium py-6 text-center">
          No platform data for the {windowLabel} window yet. AdImpression hasn't logged any deviceType in this period.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Donut summary */}
          <div className="lg:col-span-5 flex flex-col items-center justify-center gap-3">
            <div className="text-center">
              <div className="uppercase text-[9px] tracking-[0.22em] text-secondary font-extrabold mb-1">Unique users · {windowLabel}</div>
              <div className="text-4xl text-fontBlack font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmt(total)}
              </div>
            </div>
            <Donut
              size={180}
              data={tiles
                .filter((t) => t.count > 0)
                .map((t) => ({ label: t.label, value: t.count, color: t.color }))}
            />
          </div>

          {/* Per-platform tiles */}
          <div className="lg:col-span-7 grid grid-cols-2 gap-3">
            {tiles.map((t) => (
              <div key={t.key} className="border border-border rounded-lg bg-white p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="rounded-sm" style={{ width: 10, height: 10, background: t.color }} />
                  <span className="uppercase text-[10px] tracking-[0.18em] text-fontBlack font-extrabold">{t.label}</span>
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl text-fontBlack font-extrabold leading-none" style={{ fontVariantNumeric: 'tabular-nums', color: t.color }}>
                    {fmt(t.count)}
                  </span>
                  <span className="text-sm text-secondary font-bold">{t.pct}%</span>
                </div>
                <Bullet value={t.count} max={Math.max(1, total)} color={t.color} height={5} />
              </div>
            ))}
          </div>
        </div>
      )}
    </SectionShell>
  );
};

// ─── Dashboard hook wrapper ──────────────────────────────────────

export const useMissionData = () => {
  return useGetMissionControl();
};
